import { WebAudioArtifactService } from "@/modules/audio-recording/web-artifacts";
import {
  AppendAudioChunkArgs,
  AudioRecordingArtifactDebugSummary,
  AudioRecordingArtifactState,
  AudioRecordingStrategyDriver,
  CompleteRecordingArgs,
  EnsureLocalRecordingStartedArgs,
  GetArtifactDebugSummaryArgs,
  GetInitialAudioChunkCountArgs,
  GetPersistedDurationMsArgs,
  GetUploadableArtifactStatusArgs,
  RemoveLocalArtifactsArgs,
  UploadableAudioRecordingArtifactStatus,
} from "./audio-recording-strategy-driver";

type WebArtifactRecordingStrategyDriverArgs = {
  webAudioArtifacts: WebAudioArtifactService;
};

export class WebArtifactRecordingStrategyDriver implements AudioRecordingStrategyDriver {
  private readonly webAudioArtifacts: WebAudioArtifactService;

  constructor({ webAudioArtifacts }: WebArtifactRecordingStrategyDriverArgs) {
    this.webAudioArtifacts = webAudioArtifacts;
  }

  async ensureLocalRecordingStarted({ audioRecordingId }: EnsureLocalRecordingStartedArgs) {
    await this.webAudioArtifacts.ensureAudioArtifactStarted({
      audioRecordingId,
    });
  }

  async getInitialAudioChunkCount({ audioRecordingId }: GetInitialAudioChunkCountArgs) {
    const snapshot = await this.webAudioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });

    return snapshot?.chunkCount ?? 0;
  }

  async appendAudioChunk({ audioRecordingId, chunk }: AppendAudioChunkArgs) {
    await this.webAudioArtifacts.appendAudioArtifactChunk({
      audioRecordingId,
      chunk: {
        blob: chunk.blob,
        byteLength: chunk.byteLength,
        durationMs: chunk.durationMs,
        mimeType: chunk.mimeType,
      },
    });
  }

  async completeRecording({ audioRecordingId }: CompleteRecordingArgs) {
    const finalizedArtifact = await this.webAudioArtifacts.finalizeAudioArtifact({
      audioRecordingId,
    });

    if (!finalizedArtifact) {
      throw new Error(`Failed to finalize web audio artifact ${audioRecordingId}`);
    }

    return finalizedArtifact.durationMs;
  }

  async getPersistedDurationMs({ audioRecordingId }: GetPersistedDurationMsArgs) {
    const snapshot = await this.webAudioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });

    return snapshot?.durationMs ?? null;
  }

  async getArtifactDebugSummary({
    audioRecordingId,
  }: GetArtifactDebugSummaryArgs): Promise<AudioRecordingArtifactDebugSummary> {
    const snapshot = await this.webAudioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });

    return {
      durationMs: snapshot?.durationMs ?? 0,
      partCount: snapshot?.chunkCount ?? 0,
      sizeBytes: snapshot?.sizeBytes ?? 0,
      state: snapshot?.state
        ? snapshot.state === "finalized"
          ? AudioRecordingArtifactState.Finalized
          : AudioRecordingArtifactState.Writing
        : undefined,
    };
  }

  async getUploadableArtifactStatus({
    audioRecordingId,
  }: GetUploadableArtifactStatusArgs): Promise<UploadableAudioRecordingArtifactStatus> {
    const artifactRef = await this.webAudioArtifacts.getAudioArtifactRef({
      audioRecordingId,
    });

    return {
      artifactSizeBytes: artifactRef?.sizeBytes ?? null,
      hasUploadableArtifact: artifactRef !== null && artifactRef.sizeBytes > 0,
    };
  }

  async removeLocalArtifacts({ audioRecordingId }: RemoveLocalArtifactsArgs) {
    await this.webAudioArtifacts.removeAudioArtifact({
      audioRecordingId,
    });
  }
}
